import {useMutation, useQuery} from '@tanstack/react-query';
import queries from './queries';
import {useParams} from 'react-router-dom';
import axios from './axios';
import {
  MetaParser,
  PassportParser,
  VisaRequestParser,
  VisitorParser,
} from '@/parsers';
import {Utility} from '@/utils';
import client from './client';
import {useRef} from 'react';

export const useMetaData = () => {
  const {visa_request_id} = useParams();
  const metaQuery = useQuery(queries.meta);
  const meta = new MetaParser(metaQuery.data!, visa_request_id);

  return meta;
};

export const useVisitor = () => {
  const {visa_request_id} = useParams();
  const api = useApiRequest();
  const lazyApi = useApiRequest(false);
  const data = useQuery(queries.visitors(visa_request_id));
  const [visitor] = data.data || [];
  const lazyTimer = useRef(0);

  const update = useMutation({
    mutationFn: (data: FormData | Record<string, string> | Visitor) => {
      const form = new FormData();
      if (data instanceof FormData) {
        return api.put('', data);
      } else {
        Object.entries(data).forEach(([key, value]) => {
          form.append(key, value);
        });
        return api.put('', form);
      }
    },
  });

  return {
    visitor: new VisitorParser(visitor),
    update: update.mutateAsync,
    lazyUpdate: (form: FormData) => {
      lazyTimer.current && clearTimeout(lazyTimer.current);
      lazyTimer.current = setTimeout(() => {
        lazyApi.put('', form);
      }, 2000);
    },
    refresh: api.refresh,
    updating: update.isPending,
    append: (callback: (visitor: Visitor) => Visitor) => {
      client.setQueryData(
        queries.visitors(visa_request_id).queryKey,
        (old?: Visitor[]) => {
          return old?.map((v: Visitor) => {
            if (v.visitor_id === visitor.visitor_id) {
              return callback(v);
            }
            return v;
          });
        },
      );
    },
    linkEntity: (id: string) => {
      const form = new FormData();
      form.append('finbox_session_id', id);
      lazyApi.put('/link_entity_id', form);
    },
  };
};

export const useRequestPayment = () => {
  const {visa_request_id} = useParams<{visa_request_id: string}>();

  const redirect = (paymentData: {redirect_url?: string}) => {
    if (paymentData?.redirect_url) {
      window.location.href = paymentData.redirect_url;
    } else {
      window.location.reload();
    }
  };

  const callPaymentApi = async (initiated_by: string) => {
    try {
      const url = `/api/visa_requests/${visa_request_id}/payments/?pay_consent=true&new=true`;
      const form = new FormData();
      form.append('initiated_by', initiated_by);
      const data = await axios.post<{redirect_url?: string}>(url, form);
      redirect(data);
    } catch (e) {
      /* empty */
      if (import.meta.env.DEV) {
        window.location.reload();
      }
    }
  };

  return {
    redirect: callPaymentApi,
    submitCoupon: ({pin, couponCode}: {pin: string; couponCode: string}) => {
      const form = new FormData();
      form.append('pin', pin);
      form.append('coupon_code', couponCode);
      form.append('initiated_by', 'agent');
      form.append('visa_request_id', visa_request_id!);
      return axios.post<{
        success: boolean;
        message: string;
      }>('/api/coupons/redeem_coupon', form);
    },
  };
};

export const usePassport = () => {
  const {visitor} = useVisitor();
  return new PassportParser(visitor.passport!, visitor);
};

export const useVisaRequest = () => {
  const {visa_request_id} = useParams();
  const data = useQuery(queries.visaRequest(visa_request_id));
  return new VisaRequestParser(data.data!);
};

export const useNewVisaRequest = () => {
  const request = useMutation({
    mutationFn: (data: FormData) => {
      return axios.post<NewVisaRequestResponse>('/visa_requests', data);
    },
  });
  return {
    request,
    update: (url: string, data: FormData) => axios.put(url, data),
  };
};

export const usePassportApi = (id: string) => {
  const api = useApiV2Request(false);
  const {append} = useVisitor();

  const update = async (data: FormData) => {
    const url = `/passports/${id}`;
    if (id) {
      await api.put<PassportUpdateResponse>(url, data);
    } else {
      await api.post<Passport>('/passports', data);
    }
    return (passport: Passport) => {
      append(visitor => {
        return {...visitor, passport};
      });
    };
  };

  return {
    update,
    removeBack: async () => {
      await api.put(`/passports/${id}/remove_back_image`);
      append(visitor => {
        return {
          ...visitor,
          passport: {
            ...visitor.passport!,
            back_image_url: '',
          },
        };
      });
    },
  };
};

export const useVisitorPhoto = (id?: string) => {
  const api = useApiRequest();
  return {
    upload: (file: File) => {
      const form = new FormData();
      form.append('image', file);
      return api.post(`/photographs`, form);
    },
    delete: () => api.delete(`/photographs/${id}`),
  };
};

export const useVisitorVisa = () => {
  const api = useApiV2Request(false);
  const {append} = useVisitor();

  return {
    upload: (data: FormData) => api.post(`/visas`, data),
    delete: async (id: string) => {
      await api.delete(`/visas/${id}`);
      append(visitor => {
        return {
          ...visitor,
          visas: visitor.visas.filter(v => v.visa_id !== id),
        };
      });
    },
    skip: async (id: string) => {
      const visa = await api.put<Visa>(`/visas/${id}/skip`);
      append(visitor => {
        const existingIndex = visitor.visas.findIndex(
          v => v.visa_id === id,
        );
        if (existingIndex >= 0) {
          const updatedVisas = [...visitor.visas];
          updatedVisas[existingIndex] = visa;
          return {...visitor, visas: updatedVisas};
        } else {
          return {...visitor, visas: [...visitor.visas, visa]};
        }
      });
      return visa;
    },
    onUpload: (visa: any) => {
      append(visitor => {
        return {...visitor, visas: [...visitor.visas, visa]};
      });
    },
  };
};

export const useVisitorItr = (id?: string) => {
  const api = useApiRequest();
  return {
    send: (data: FormData) =>
      api.post<{id: string}>(`/itr_verifications`, data),
    verify: (data: FormData) => api.post(`/itr_verifications/submit_otp`, data),
    dLink: () => api.delete(`/itr_verifications/${id}`),
    refresh: api.refresh,
  };
};

export const useSupportingDocuments = () => {
  const api = useApiRequest();
  return {
    upload: (data: FormData) => api.post(`/supporting_documents`, data),
    delete: (id: string) => api.delete(`/supporting_documents/${id}`),
  };
};

export const useBanks = (query: string) => {
  const api = useApiRequest(false);
  const data = useQuery({
    queryKey: ['banks', query],
    queryFn: async () => {
      const url = `/bank_statements/banks?query=${query}`;
      const data = await api.get<Bank[]>(url);
      if (data.length === 0) {
        return [
          {
            bank_id: {
              $oid: 'new',
            },
            name: "Can't find your bank? Add Here",
            full_name: "Can't find your bank? Add Here",
            obj_id: 'new',
            logo_dark_url: '',
            country_code: '',
          },
        ];
      } else {
        return data;
      }
    },
  });
  return data.data || [];
};

export const useBankStatements = () => {
  const api = useApiRequest(false);
  const {append} = useVisitor();
  return {
    upload: (data: FormData) =>
      api.post<{
        error_code?: string;
        bank_statement_id?: string;
        errors?: string[];
      }>(`/bank_statements`, data),
    remove: async (id: string) => {
      await api.delete(`/bank_statements/${id}`);
      api.refresh();
      append(visitor => {
        return {
          ...visitor,
          bank_statements: (visitor.bank_statements || []).filter(
            s => s.bank_statement_id !== id,
          ),
        };
      });
    },
    onUpload: api.refresh,
  };
};

export const useTripSponsor = (id?: string) => {
  const api = useApiRequest();
  return {
    create: (data: FormData) => api.post('/supporting_documents', data),
    update: (data: FormData) => api.put(`/supporting_documents/${id}`, data),
    save(data: FormData) {
      return id ? this.update(data) : this.create(data);
    },
    delete: (id: string) => api.delete(`/supporting_documents/${id}`),
  };
};

export const useVisitorAadhar = () => {
  const {visitor_id = ''} = useParams();
  const {visitor} = useVisitor();
  const api = useApiRequest();
  const BASE_URL = '/api/aadhar_verifications';

  return {
    sendOtp: (data: FormData) => {
      data.append('visitor_id', visitor_id);
      return axios.post<{id?: string}>(BASE_URL, data);
    },
    verifyOtp: (data: FormData) => {
      return axios.post(`${BASE_URL}/${visitor_id}/verify`, data);
    },
    dLink: async () => {
      const url = `${BASE_URL}/${visitor_id}/?visitor_id=${visitor_id}`;
      await axios.delete(url);
      api.refresh();
    },
    loginWithDLocker() {
      return axios.post<{url: string}>(
        BASE_URL + '/' + visitor_id + '/login_digilocker',
      );
    },
    onOtpVerify: api.refresh,
    linked: !!visitor.aadhar_linked,
    ask: visitor.ask_for_adhar,
    required: visitor.adhar_required,
    details: visitor.visitor_aadhar!,
  };
};

export const useSponsorDocumentVerification = () => {
  const {visitor} = useVisitor();
  const api = useApiV2Request(false);
  const BASE_URL = '/supporting_documents';

  return {
    sendOtp: async (data: FormData) => {
      data.append('document_type', 'aadhar_card');
      data.append('category', 'bank_sponsor');
      return api.post<{id: string}>(`${BASE_URL}/send_aadhar_otp`, data);
    },
    verifyOtp: (data: FormData, id?: string) => {
      return api.post(`${BASE_URL}/${id}/submit_aadhar_otp`, data);
    },
    passport: visitor.sponsor_passport,
    removePassport: async (id: string) => {
      if (id) {
        await api.delete(`${BASE_URL}/${id}`);
        api.refresh();
      }
    },
    dLink: async () => {
      const id = visitor.sponsor_verified_document?.obj_id;
      const url = `${BASE_URL}/${id}/`;
      await api.delete(url);
      api.refresh();
    },
    submitItrDetails: async (data: FormData) => {
      data.append('document_type', 'itr');
      data.append('category', 'bank_sponsor');
      return api.post<{id: string}>(`${BASE_URL}/itr_client_create`, data);
    },
    verifyItrOtp: async (data: FormData) => {
      data.append('document_type', 'itr');
      data.append('category', 'bank_sponsor');
      return api.post(`${BASE_URL}/itr_client_submit_otp`, data);
    },
    uploadPassport: async (data: FormData) => {
      data.append('category', 'bank_sponsor');
      return api.post<{id: string}>(`${BASE_URL}/upload_passport`, data);
    },
    verifyGstNumber: async (data: FormData) => {
      data.append('document_type', 'gst_certificate');
      data.append('category', 'bank_sponsor');
      return api.post<{id: string}>(`${BASE_URL}/link_gst`, data);
    },
    removeGst: async (id: string) => {
      if (id) {
        await api.delete(`${BASE_URL}/${id}`);
        api.refresh();
      }
    },
    uploadGst: async (data: FormData) => {
      return api.post(`${BASE_URL}/upload_gst`, data);
    },
    uploadMarriageCertificate: async (data: FormData) => {
      return api.post(`${BASE_URL}/upload_marriage_certificate`, data);
    },
    onOtpVerify: api.refresh,
    linked: !!visitor.sponsor_document_linked,
    ask: visitor.ask_for_adhar,
    required: visitor.adhar_required,
    details: visitor.sponsor_verified_document!,
    details_type: visitor.sponsor_details_type,
  };
};

export const useApiV2Request = (refetch = true) => {
  return useBaseApi('api2', refetch);
};

export const useApiRequest = (refetch = true) => {
  return useBaseApi('api', refetch);
};

const useBaseApi = (ver: string, refetch = true) => {
  const {visa_request_id, visitor_id} = useParams();

  return {
    path: `/${ver}/visa_requests/${visa_request_id}/visitors/${visitor_id}`,
    refresh: () => {
      return client.refetchQueries(queries.visitors(visa_request_id));
    },
    async request(url: string, method: string, data?: FormData) {
      const res = await axios.request(this.path + url, method, data);
      if (refetch) {
        this.refresh();
      }
      return res;
    },
    get<T>(url: string) {
      return this.request(url, 'GET') as Promise<T>;
    },
    post<T>(url: string, body?: FormData) {
      return this.request(url, 'POST', body) as Promise<T>;
    },
    put<T>(url: string, body?: FormData) {
      return this.request(url, 'PUT', body) as Promise<T>;
    },
    async delete(url: string) {
      const URL = this.path + url;
      await axios.delete(URL);
      if (refetch) {
        this.refresh();
      }
    },
    downloadReceipt() {
      const url = `/${ver}/visa_requests/${visa_request_id}/thank_you`;
      return axios.get<{pdf_url: string}>(url);
    },
    async submit() {
      Utility.showLoader();
      try {
        const response = await axios.put<{
          success?: boolean;
          message?: string;
          status?: VisaRequest['status'];
        }>(
          `/${ver}/visa_requests/${visa_request_id}`,
        );
        if (response.success !== false) {
          client.setQueryData(
            queries.visaRequest(visa_request_id).queryKey,
            (old?: VisaRequest) =>
              old
                ? {...old, status: response.status || 'submitting'}
                : old,
          );
          await client.refetchQueries(queries.visaRequest(visa_request_id));
        }
        return response;
      } finally {
        Utility.hideLoader();
      }
    },
  };
};
